<template>
  <div
    v-if="searchActive"
    class="search-overlay"
    :style="getOverlayStyles"
  >
    <div class="container">
      <ais-configure
        :hits-per-page.camel="6"
      />

      <div
        v-if="
          (!currentRefinement && popularSearchLinkList?.items.length) ||
            (currentRefinement && !hasAnyResults)
        "
        class="search-overlay__no-results-container"
      >
        <p
          v-if="currentRefinement && !hasAnyResults"
          class="
            search-overlay__no-results-total
            text-body-sbold
            text-heading-m-desktop
          "
          v-text="$string('search.search_overlay.no_results_full')"
        />

        <template
          v-if="popularSearchLinkList?.items.length"
        >
          <h3
            class="search-overlay__index-heading text-body-sbold"
            v-text="$string('search.search_overlay.popular_header')"
          />

          <ul class="search-overlay__text-list">
            <li
              v-for="(tier1) in popularSearchLinkList.items"
              :key="tier1.handle"
            >
              <a
                class="text-body-s"
                :href="tier1.url"
                tabindex="0"
                v-text="tier1.title"
              />
            </li>
          </ul>
        </template>
      </div>

      <template v-if="currentRefinement && hasAnyResults">
        <div class="search-overlay__grid grid">
          <div class="search-overlay__mobile-buttons col xs4">
            <div
              v-for="indexType in mobileButtonIndexOrder"
              :key="indexType"
              class="search-overlay__grid-item"
              :class="`search-overlay__grid-item--${indexType}`"
            >
              <h3
                class="
                  search-overlay__index-heading
                  text-body-xs
                "
                v-text="headers[indexType]"
              />

              <div
                v-if="getIndexData(indexType)?.hits?.length"
                class="search-overlay__button-rows"
              >
                <a
                  v-for="hit in getIndexData(indexType).hits"
                  :key="hit.ObjectID"
                  class="search-overlay__button text-body-s"
                  :href="getItemData(hit, indexType).url"
                  tabindex="0"
                  v-text="getItemData(hit, indexType).label"
                />
              </div>

              <p
                v-else
                class="search-overlay__no-results text-body-s"
                v-text="$string('search.search_overlay.no_results_single')"
              />
            </div>
          </div>

          <div
            class="
              search-overlay__grid-item
              search-overlay__grid-item--other
              search-overlay__grid-item--desktop
              col
              xs4
              l2-5
            "
          >
            <div
              v-for="indexType in desktopOtherIndexOrder"
              :key="indexType"
              class="search-overlay__results"
              :class="`search-overlay__results--${indexType}`"
            >
              <h3
                class="
                  search-overlay__index-heading
                  text-body-xs
                  text-body-sbold-desktop
                "
                v-text="headers[indexType]"
              />

              <ul
                v-if="getIndexData(indexType)?.hits?.length"
                class="search-overlay__text-list"
              >
                <li
                  v-for="hit in getIndexData(indexType).hits"
                  :key="hit.objectID"
                >
                  <a
                    class="text-body-s"
                    :href="getItemData(hit, indexType).url"
                    tabindex="0"
                  >
                    <ais-highlight
                      :hit="hit"
                      :attribute="getItemData(hit, indexType).highlightAttribute"
                      highlighted-tag-name="span"
                    />

                    <span
                      v-if="getItemData(hit, indexType).count"
                      class="search-overlay__item-count"
                      v-text="`(${getItemData(hit, indexType).count})`"
                    />
                  </a>
                </li>
              </ul>

              <p
                v-else
                class="search-overlay__no-results text-body-s"
                v-text="$string('search.search_overlay.no_results_single')"
              />
            </div>
          </div>

          <div
            class="
              search-overlay__grid-item
              search-overlay__grid-item--products
              col
              xs4
              l6-11
            "
          >
            <h3
              class="
                  search-overlay__index-heading
                  text-body-xs
                  text-body-sbold-desktop
                "
              v-text="headers.product"
            />

            <ul
              v-if="getIndexData('product').hits?.length"
              class="search-overlay__product-grid"
            >
              <li
                v-for="hit in formatProductData(getIndexData('product').hits)"
                :key="hit.objectID"
                class="search-overlay__product"
              >
                <a
                  class="search-overlay__product-link"
                  :href="getItemData(hit, 'product').url"
                  tabindex="0"
                >
                  <responsive-image
                    block-class="search-overlay"
                    :image="{
                      src: hit.image,
                      alt: hit.title,
                    }"
                    min-max="60-100"
                    ratio="1:1"
                  />

                  <div class="search-overlay__product-details">
                    <p
                      class="search-overlay__product-vendor text-body-xs"
                      v-text="hit.vendor"
                    />

                    <p
                      class="search-overlay__product-title text-body-xs"
                      v-text="hit.title"
                    />

                    <product-card-swatches :product="hit" />

                    <product-prices
                      :class="$blockClass('search-overlay-price')"
                      :available="hit?.available"
                      block-class="product-card"
                      :compare-at-price="
                        hit?.compareAtPrice?.amount ?? hit?.compareAtPrice
                      "
                      :price="hit?.price?.amount"
                      component-location="searchOverlayProductCard"
                    />
                  </div>
                </a>
              </li>
            </ul>

            <p
              v-else
              class="search-overlay__no-results text-body-s"
              v-text="$string('search.search_overlay.no_results_single')"
            />

            <ais-stats class="search-overlay__product-button-container">
              <template #default="{ nbHits }">
                <btn
                  v-show="nbHits > 0"
                  class="search-overlay__product-button"
                  block-class="search-overlay-product"
                  :url="getQueryPath(currentRefinement)"
                  modifiers="secondary"
                  :show-icon="false"
                  tabindex="0"
                  :label="$string('search.search_overlay.view_all')
                    .replace('{{ count }}', nbHits)"
                  @click="searchBarDataLayer(currentRefinement)"
                />
              </template>
            </ais-stats>
          </div>

          <div
            class="
              search-overlay__grid-item
              search-overlay__grid-item--pages
              search-overlay__grid-item--mobile
              col
              xs4
            "
          >
            <h3
              class="search-overlay__index-heading text-body-xs"
              v-text="headers.page"
            />

            <ul
              v-if="getIndexData('page')?.hits?.length"
              class="search-overlay__text-list"
            >
              <li
                v-for="hit in getIndexData('page').hits"
                :key="hit.ObjectID"
              >
                <a
                  class="text-body-s"
                  :href="getItemData(hit, 'page').url"
                  tabindex="0"
                >
                  <ais-highlight
                    :hit="hit"
                    :attribute="getItemData(hit, 'page').highlightAttribute"
                    highlighted-tag-name="span"
                  />

                  <span
                    v-if="getItemData(hit, 'page').count"
                    class="search-overlay__item-count"
                    v-text="`(${getItemData(hit, 'page').count})`"
                  />
                </a>
              </li>
            </ul>

            <p
              v-else
              class="search-overlay__no-results text-body-s"
              v-text="$string('search.search_overlay.no_results_single')"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Search overlay (search-overlay)
 * -----------------------------------------------------------------------------
 * Search results overlay.
 *
 * @param {String} currentRefinement - Current value of the query.
 * @param {Object} indices - List of indices.
 * @param {Boolean} searchActive - If the search input is focused.
 * @param {Object} popularSearchLinkList - Link list to display on search input focus.
 *
 */

import { nextTick } from 'vue'
import { AisConfigure, AisHighlight, AisStats } from 'vue-instantsearch/vue3/es'
import { algoliaIndicies } from '~/config/algolia'
import Btn from '~global/btn/btn.vue'
import ResponsiveImage from '~global/images/responsive-image'
import { formatAlgoliaProduct } from '~/helpers/format'

export default {
  name: 'SearchOverlay',

  components: {
    AisConfigure,
    AisHighlight,
    AisStats,
    ResponsiveImage,
    Btn,
  },

  props: {
    currentRefinement: {
      type: String,
      default: '',
    },
    indices: {
      type: Object,
      default: () => ({}),
    },
    searchActive: {
      type: Boolean,
      default: false,
    },
    overlayStart: {
      type: Number,
      default: 0,
    },
    popularSearchLinkList: {
      type: Object,
      default: () => ({
        items: [],
      }),
    },
  },

  emits: ['results-updated'],

  data() {
    return {
      global: true,
      headers: {
        product: this.$string('search.search_overlay.products_header'),
        suggestion: this.$string('search.search_overlay.suggestions_header'),
        page: this.$string('search.search_overlay.pages_header'),
        collection: this.$string('search.search_overlay.collections_header'),
      },
      algoliaIndicies,
      mobileButtonIndexOrder: ['suggestion', 'collection'],
      desktopOtherIndexOrder: [
        'collection',
        'page',
        'suggestion',
      ],
    }
  },

  computed: {

    /**
     * Get overlay positioning styles when no results/no query state is active
     */
    getOverlayStyles() {
      if (
        (!this.currentRefinement || !this.hasAnyResults) &&
        this.overlayStart > 0
      ) {
        const overlayStartPadding = `1.5rem`

        return {
          width: `calc(100% - ${this.overlayStart}px + ${overlayStartPadding})`,
          insetInlineStart: `calc(${this.overlayStart}px - ${overlayStartPadding}`,
        }
      }

      return null
    },

    /**
     * Compute if any of the Algolia indices have results.
     * @returns {boolean}
     */
    hasAnyResults() {
      return this.indices.some(indexData => indexData?.hits.length)
    },
  },

  watch: {
    indices() {
      nextTick(() => {
        this.$emit('results-updated', {
          indicies: this.indices,
          hasResults: this.hasAnyResults,
        })
      })
    },
  },

  methods: {

    formatProductData(items) {
      const formattedHits = items.map((item) => formatAlgoliaProduct(item))

      return formattedHits
    },

    getIndexData(key) {
      const indexName = algoliaIndicies[key]
      if (!indexName) return []

      const indexData = this.indices.find((index) => index.indexName === indexName)
      return indexData
    },

    /**
     * Get formatted data for hits based on index type
     */
    getItemData(item, indexType) {
      const urlRoot = cnvs.routes.root === '/' ? '' : cnvs.routes.root

      switch (indexType) {
        case 'collection':
          return {
            url: `${urlRoot}/collections/${item.handle}`,
            label: `${item.title} (${item.products_count})`,
            highlightAttribute: 'title',
            count: item.products_count ?? null,
          }
        case 'page':
          return {
            url: `${urlRoot}/pages/${item.handle}`,
            label: item.title,
            highlightAttribute: 'title',
          }
        case 'product':
          return {
            url: `${urlRoot}/products/${item.handle}`,
          }
        case 'suggestion':
          return {
            url: `${cnvs.routes.search}?query=${item.query}`,
            label: item.query,
            highlightAttribute: 'query',
          }
      }
    },

    /**
     * Returns the base query path.
     * @param {string} query - The query to override.
     * @returns {string} - The path to the search results page.
     */
    getQueryPath(query) {
      return query
        ? `/search?query=${query}`
        : '/search'
    },

    /**
     * Send the click event info to the data layer.
     * @param {String} label - search term.
     */
    searchBarDataLayer(label) {
      cnvs.EventBus.emit('Datalayer:searchBar', label.toLowerCase())
    },
  },
}
</script>

<style lang="scss">
@import './search-overlay';
</style>
